// @greenlight-spec: 12-azure-installation.md
// @greenlight-spec: 28-it-dashboard-ui.md

import type { RoleSource } from '@greenlight/shared';

import { DOCS_LINKS } from '@/lib/docsLinks';

/**
 * IdP provider registry. Adding a provider is a single entry here — the wizard
 * step renders whatever is registered, so new OIDC providers slot in without
 * touching the step component. `available: false` keeps a provider visible as a
 * "coming soon" option without offering its config form yet.
 *
 * MVP provisions exactly one provider per org (re-running replaces it);
 * Microsoft Entra ID, Google Workspace, Okta, and a generic OpenID Connect
 * option for any other spec-compliant issuer are the supported providers
 * (docs/10 §Supported IdPs, docs/12 §IdP OIDC setup).
 */
export type IdpProviderId = 'entra' | 'google' | 'okta' | 'oidc';

/**
 * One line of the inline setup checklist. A step carries a console URL only
 * where that URL is stable for every tenant; anything tenant-specific stays
 * prose rather than shipping a link that 404s.
 */
export type IdpSetupStep = string | { readonly text: string; readonly href: string };

export function setupStepText(step: IdpSetupStep): string {
  return typeof step === 'string' ? step : step.text;
}

export function setupStepHref(step: IdpSetupStep): string | null {
  return typeof step === 'string' ? null : step.href;
}

export interface TenantIssuerDef {
  label: string;
  placeholder: string;
  help: string;
  toIssuer: (tenant: string) => string;
  /** The tenant a template-shaped issuer embeds, or null for any other issuer. */
  fromIssuer: (issuer: string) => string | null;
}

const ENTRA_PUBLIC_AUTHORITY = 'https://login.microsoftonline.com';

export function entraIssuerForTenant(tenant: string): string {
  const t = tenant.trim();
  return t === '' ? '' : `${ENTRA_PUBLIC_AUTHORITY}/${t}/v2.0`;
}

export function entraTenantFromIssuer(issuer: string): string | null {
  const match = /^https:\/\/login\.microsoftonline\.com\/([^/\s]+)\/v2\.0\/?$/i.exec(issuer.trim());
  return match?.[1] ?? null;
}

export interface IdpProviderDef {
  id: IdpProviderId;
  /** Display name shown on the selection card and summary. */
  label: string;
  /** One-line description for the selection card. */
  blurb: string;
  /** Monogram letter for the provider tile (brand-neutral; on-system tokens). */
  glyph: string;
  /** Public per-provider setup guide (screenshots of each admin-console click). */
  docsUrl: string;
  /** Example issuer URL shown as the field placeholder. */
  issuerPlaceholder: string;
  /** Pre-filled into the issuer field on select, for providers whose issuer
   * is fixed (still editable — tests and sovereign clouds override it). */
  defaultIssuer?: string;
  /** Short hint under the issuer field. */
  issuerHelp: string;
  /** Collects a tenant ID and derives the issuer from it; the full issuer
   * field stays reachable for issuers the template does not cover. */
  tenantIssuer?: TenantIssuerDef;
  /** Default OIDC group claim; overridable per install (docs/10 §SSO/RBAC). */
  defaultGroupClaim: string;
  /** Hint under the admin-group field — the identifier format the provider's
   * group claim emits, which mappings must match verbatim (docs/10 §IdP group
   * → platform role mapping). */
  adminGroupHelp: string;
  /** Hint under the user-group (sign-in restriction) field — empty means
   * allow-all within the org; set it to gate sign-in (docs/10 §IdP group →
   * platform role mapping). */
  userGroupHelp: string;
  /**
   * Inline checklist of admin-console actions to perform in the IdP. A function
   * of the chosen role source: the groups-claim step's emphasis (or presence)
   * depends on whether roles are IdP-managed (docs/10 §IdP group → platform
   * role mapping) — skipping it under `idp` leaves Entra issuing no `groups`
   * claim at all, which refuses sign-in for everyone regardless of
   * `role_groups` (docs/12 §Wizard-only window).
   */
  setupSteps: (roleSource: RoleSource) => readonly IdpSetupStep[];
  /** Collects a sign-in button label — for a provider with no brand to render. */
  collectsDisplayName?: boolean;
  /** Role source pre-selected when the provider is chosen (default `idp`; Google is forced to `greenlight`). */
  defaultRoleSource?: RoleSource;
  /** Guidance rendered under the role-source picker. */
  roleSourceNote?: string;
  /** False ⇒ rendered disabled as a "coming soon" option. */
  available: boolean;
}

export const IDP_PROVIDERS: readonly IdpProviderDef[] = [
  {
    id: 'entra',
    label: 'Microsoft Entra ID',
    blurb: 'Azure AD / Microsoft 365 single sign-on.',
    glyph: 'M',
    docsUrl: DOCS_LINKS.idpInstallEntra,
    issuerPlaceholder: 'https://login.microsoftonline.com/<tenant-id>/v2.0',
    issuerHelp:
      'The v2.0 issuer for your tenant. Use this for a sovereign cloud (US Gov, China) whose sign-in host is not login.microsoftonline.com.',
    tenantIssuer: {
      label: 'Directory (tenant) ID',
      placeholder: '00000000-0000-0000-0000-000000000000',
      help: "The Directory (tenant) ID on your app registration's Overview page.",
      toIssuer: entraIssuerForTenant,
      fromIssuer: entraTenantFromIssuer,
    },
    defaultGroupClaim: 'groups',
    adminGroupHelp:
      'Members get the org admin role at sign-in. Use the group object ID (a UUID) from the Entra admin center.',
    userGroupHelp:
      'Leave blank to let anyone in your tenant sign in as a user. Set it to restrict sign-in to this group — the group object ID (a UUID).',
    setupSteps: (roleSource) => [
      {
        text: 'In the Entra admin center, register a new app named "Greenlight" (single tenant) — this display name is what users see on the Microsoft sign-in/consent screen.',
        href: 'https://entra.microsoft.com',
      },
      'Add the redirect URI above under Authentication → Web.',
      'Create a client secret under Certificates & secrets.',
      roleSource === 'idp'
        ? 'Under Token configuration → Add groups claim, choose "Groups assigned to the application" — required for IdP-managed role mapping. Without a groups claim, no one gets a mapped role and a user sign-in group refuses everyone. Avoid "Security groups": it leaves out Microsoft 365 groups and drops the whole claim for users in more than 200 groups.'
        : 'Add a groups claim ("Groups assigned to the application") under Token configuration if you set a user sign-in group below or plan to switch to IdP-managed roles later; otherwise not required while roles are Greenlight-managed.',
      'Under Enterprise applications → Greenlight → Users and groups, assign every group you enter below. Only assigned groups reach the token, and only for their direct members. Assigning groups needs Entra ID P1 or P2; without it, use "Security groups" and keep your Greenlight groups to security groups.',
      'Optional: paste the Sign-out redirect above into Front-channel logout URL under Authentication.',
      'Copy the Directory (tenant) ID, Application (client) ID, and secret value into the form.',
    ],
    available: true,
  },
  {
    id: 'google',
    label: 'Google Workspace',
    blurb: 'Google Cloud OAuth for Workspace domains.',
    glyph: 'G',
    docsUrl: DOCS_LINKS.idpInstallGoogle,
    issuerPlaceholder: 'https://accounts.google.com',
    defaultIssuer: 'https://accounts.google.com',
    issuerHelp: 'Google uses a fixed issuer; configure the OAuth client in Google Cloud console.',
    defaultGroupClaim: 'groups',
    adminGroupHelp:
      'Members get the org admin role at sign-in. Use the group email address, e.g. it-admins@acme.com.',
    userGroupHelp:
      'Leave blank to let anyone in your Workspace domain sign in as a user. Set it to restrict sign-in to this group — the group email address, e.g. greenlight-users@acme.com.',
    setupSteps: () => [
      {
        text: 'In Google Cloud console, configure the OAuth consent screen (internal).',
        href: 'https://console.cloud.google.com/apis/credentials/consent',
      },
      {
        text: 'Under Credentials, create an OAuth 2.0 Client ID of type "Web application".',
        href: 'https://console.cloud.google.com/apis/credentials',
      },
      'Add the redirect URI below under Authorized redirect URIs.',
      'Download the client JSON and import it below, or copy the client ID and client secret into the form.',
    ],
    available: true,
  },
  {
    id: 'okta',
    label: 'Okta',
    blurb: 'Okta OIDC single sign-on.',
    glyph: 'O',
    docsUrl: DOCS_LINKS.idpInstallOkta,
    issuerPlaceholder: 'https://<your-org>.okta.com',
    issuerHelp: 'Okta org issuer URL.',
    defaultGroupClaim: 'groups',
    adminGroupHelp:
      "Members get the org admin role at sign-in. Use the exact value your authorization server's groups claim emits (commonly the group name).",
    userGroupHelp:
      'Leave blank to let anyone assigned the app sign in as a user. Set it to restrict sign-in to this group — the exact value your groups claim emits (commonly the group name).',
    setupSteps: (roleSource) => [
      'In the Okta admin console, go to Applications → Applications → Create App Integration and choose OIDC — OpenID Connect, application type Web Application. Name it "Greenlight" — this display name is what users see at sign-in.',
      'Paste the Redirect URI below under Sign-in redirect URIs, and the Sign-out redirect under Sign-out redirect URIs.',
      'Under Assignments, assign the users or groups that should have Greenlight access.',
      roleSource === 'idp'
        ? 'On the app\'s Sign On tab, set the groups claim filter (for example "groups" · Matches regex · .*) so ID tokens carry group memberships — required for IdP-managed role mapping. Without it, Okta issues sign-in tokens with no groups claim, so no one is granted a mapped role and a configured user sign-in group refuses everyone.'
        : "On the app's Sign On tab, set the groups claim filter if you plan to switch to IdP-managed roles later; not required while roles are Greenlight-managed.",
      "Copy the Client ID and client secret from the app's General tab into the form.",
    ],
    available: true,
  },
  {
    id: 'oidc',
    label: 'Other OpenID Connect provider',
    blurb: 'Any spec-compliant OIDC issuer — Authentik, Keycloak, Auth0, PingFederate, and more.',
    glyph: 'ID',
    docsUrl: DOCS_LINKS.idpInstallGenericOidc,
    issuerPlaceholder: 'https://auth.<company>.com/',
    issuerHelp:
      'The issuer URL your provider publishes; it must serve /.well-known/openid-configuration over HTTPS. For Authentik this is the per-application issuer, e.g. https://auth.acme.com/application/o/greenlight/.',
    defaultGroupClaim: 'groups',
    adminGroupHelp:
      'Members get the org admin role at sign-in. Use the exact value your provider emits in its groups claim (often the group name).',
    userGroupHelp:
      'Leave blank to let anyone your provider admits sign in as a user. Set it to restrict sign-in to this group — the exact value your groups claim emits.',
    collectsDisplayName: true,
    defaultRoleSource: 'greenlight',
    roleSourceNote:
      'Choose Greenlight-managed unless your provider emits a groups claim in the ID token. IdP-managed roles depend on that claim, so after you save, this step asks you to sign in once to prove it; that test sign-in is refused if the ID token carries no groups claim.',
    setupSteps: (roleSource) => [
      'In your provider\'s admin console, create a confidential OAuth 2.0 / OpenID Connect client (often called an application or web client) named "Greenlight".',
      'Register the Redirect URI below as an allowed redirect URI, and the Sign-out redirect where your provider supports one.',
      'Confirm the issuer URL: opening <issuer>/.well-known/openid-configuration in a browser must return JSON whose "issuer" is the same URL.',
      roleSource === 'idp'
        ? "Make sure ID tokens carry a groups claim (check your provider's claim or scope mapping) and note its name — enter it as the group claim below. Without it, the test sign-in is refused (auth.group_claim_missing) and this step cannot complete."
        : 'If you later want IdP-managed roles, configure your provider to emit a groups claim in the ID token; not required while roles are Greenlight-managed.',
      'Copy the client ID and client secret into the form, and choose the name users should see on the sign-in button.',
    ],
    available: true,
  },
];

export function getIdpProvider(id: string | undefined): IdpProviderDef | undefined {
  return IDP_PROVIDERS.find((p) => p.id === id);
}
